import React, { useState, useEffect, useMemo } from 'react';
import { FaArrowLeft, FaFilter } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchToken } from '../../../../lib/globalApiClient';
import './style.css';

const API_URL = process.env.REACT_APP_APIUrl1688;

const FindSimilar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);

  const productFromState = location.state?.product;
  const offerIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('id');
  }, [location.search]);

  const convertImageToBase64 = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/jpeg');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

  const fetchProductDetail = async (offerId) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_URL}/product-detail-by-offerid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          offerId: offerId.toString(),
          country: 'en'
        })
      });

      const data = await response.json();

      if (data.result?.success) {
        const detail = data.result.result;
        return {
          id: detail.offerId || offerId,
          title: detail.subjectTrans || detail.subject,
          image: detail.productImage?.images?.[0] || detail.imageUrl,
          price: {
            rmb: parseFloat(detail.productSaleInfo?.priceRangeList?.[0]?.price || 0),
            inr: parseFloat(detail.productSaleInfo?.priceRangeList?.[0]?.price || 0) * 12.5
          },
          supplier: detail.companyName || 'Supplier'
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      return null;
    }
  };

  const findSimilarProducts = async (product, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      console.log('Starting similar product search for:', product, 'page:', page);

      const imageBase64 = await convertImageToBase64(product.image);
      console.log('Image converted to base64, length:', imageBase64.length);

      const token = await fetchToken();
      const response = await fetch(`${API_URL}/get-product-info-by-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          imageBase64: imageBase64,
          beginPage: page,
          pageSize: 20,
          country: 'en'
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response:', data);
      console.log('Full response structure:', JSON.stringify(data, null, 2));

      // Handle the actual API response structure
      let productsData = null;

      if (data.status === 'success' && data.products) {
        productsData = data.products;
      } else if (data.result?.success && data.result?.result?.data) {
        productsData = data.result.result.data;
      } else if (data.products) {
        productsData = data.products;
      } else if (data.data) {
        productsData = data.data;
      } else if (Array.isArray(data)) {
        productsData = data;
      }

      if (productsData && Array.isArray(productsData) && productsData.length > 0) {
        const products = productsData.map((item, index) => ({
          id: item.offerId || item.id || `product-${index}`,
          title: item.subjectTrans || item.subject || item.title || 'Product Title',
          image: item.imageUrl || item.image || item.productImage?.images?.[0] || '',
          price: {
            rmb: parseFloat(item.priceInfo?.price || item.price || 0),
            inr: parseFloat(item.priceInfo?.price || item.price || 0) * 12.5
          },
          similarity: Math.max(95 - index * 2, 70),
          supplier: item.detailed_info?.productShippingInfo?.sendGoodsAddressText || item.supplier || item.companyName || 'Supplier',
          monthSold: item.monthSold || 0,
          tradeScore: item.tradeScore || 0
        }));
        console.log('Processed products:', products.length, products);

        // Update pagination info
        if (data.pagination) {
          setTotalPages(data.pagination.totalPage || 1);
          setHasMoreProducts(page < (data.pagination.totalPage || 1));
        }

        if (append) {
          setSimilarProducts(prev => [...prev, ...products]);
        } else {
          setSimilarProducts(products);
        }
      } else {
        console.log('No products found in response');
        console.log('Products data:', productsData);
        console.log('Response structure:', JSON.stringify(data, null, 2));
        if (!append) {
          setSimilarProducts([]);
        }
      }
    } catch (error) {
      console.error('Find similar failed:', error);
      setSimilarProducts([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProducts = async () => {
    if (!originalProduct || loadingMore || !hasMoreProducts) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await findSimilarProducts(originalProduct, nextPage, true);
  };

  useEffect(() => {
    const initializePage = async () => {
      let product = productFromState;

      if (!product && offerIdFromQuery) {
        product = await fetchProductDetail(offerIdFromQuery);
      }

      if (product) {
        setOriginalProduct(product);
        setCurrentPage(1);
        setHasMoreProducts(false);
        await findSimilarProducts(product, 1, false);
      }
    };

    initializePage();
  }, [productFromState, offerIdFromQuery]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...similarProducts];

    // Apply price range filter
    if (priceRange !== 'all') {
      const [min, max] = priceRange === '200+' ? [200, Infinity] : priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = product.price.rmb;
        return price >= min && (max === Infinity || price <= max);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price.rmb - b.price.rmb;
        case 'price-high':
          return b.price.rmb - a.price.rmb;
        case 'similarity':
          return b.similarity - a.similarity;
        case 'relevance':
        default:
          return 0;
      }
    });

    return filtered;
  }, [similarProducts, sortBy, priceRange]);

  if (!originalProduct) {
    return <div className="loading">Loading product details...</div>;
  }



  return (
    <div className="find-similar-container">
      <div className="similar-header">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft /> Back
        </button>
        <h2>Find Similar Products</h2>
      </div>

      <div className="reference-product">
        <h3>Reference Product:</h3>
        <div className="ref-product-card">
          <img src={originalProduct.image} alt={originalProduct.title} referrerPolicy="no-referrer" />
          <div className="ref-info">
            <h4>{originalProduct.title}</h4>
            <div className="price-display">
              <span className="rmb-price">¥{originalProduct.price.rmb.toFixed(2)}</span>
              <span className="inr-price">₹{originalProduct.price.inr.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="relevance">Relevance</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="similarity">Similarity</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Price Range:</label>
          <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
            <option value="all">All Prices</option>
            <option value="0-50">¥0 - ¥50</option>
            <option value="50-100">¥50 - ¥100</option>
            <option value="100-200">¥100 - ¥200</option>
            <option value="200+">¥200+</option>
          </select>
        </div>
      </div>

      <div className="similar-results">
        <div className="results-header">
          <span>{filteredAndSortedProducts.length} similar products found</span>
          {similarProducts.length !== filteredAndSortedProducts.length && (
            <span> (filtered from {similarProducts.length})</span>
          )}
          {loading && <span> - Searching...</span>}
          {!loading && filteredAndSortedProducts.length === 0 && <span> - No similar products available</span>}
        </div>

        <div className="similar-products-grid">
          {loading && (
            <div className="loading-message" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
              <p>Searching for similar products...</p>
            </div>
          )}
          {!loading && filteredAndSortedProducts.map(product => (
            <div
              key={product.id}
              className="similar-product-card"
              onClick={() => window.open('/admin/internal-users/product-detail?id=' + product.id, '_blank')}
              style={{ cursor: 'pointer' }}
            >
              <div className="similarity-badge">
                {product.similarity}% match
              </div>

              <div className="product-image">
                <img
                  src={product.image || '/placeholder-image.svg'}
                  alt={product.title}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.svg';
                  }}
                />
              </div>

              <div className="product-info">
                <h4>{product.title}</h4>
                <p className="supplier">{product.supplier}</p>

                <div className="price-display">
                  <span className="rmb-price">¥{product.price.rmb.toFixed(2)}</span>
                  <span className="inr-price">₹{product.price.inr.toFixed(2)}</span>
                </div>

                <div className="product-actions">
                  <button
                    className="view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/admin/internal-users/product-detail?id=${product.id}`, '_blank');
                    }}
                  >
                    View Details
                  </button>
                  <button
                    className="compare-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/admin/internal-users/find-similar?id=${product.id}`, '_blank');
                    }}
                  >
                    Find Similar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasMoreProducts && (
        <div className="load-more-section">
          <button
            className="load-more-btn"
            onClick={loadMoreProducts}
            disabled={loading || loadingMore}
          >
            {loadingMore ? 'Loading More...' : 'Load More Products'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FindSimilar;